var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { ConsentStatus, IdentifiedPatientState, primaryConsentTypes } from "healthex-common/consent.dtos";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { ProjectType, StudyPatientConsentStatusSearch, StudyStatus } from "healthex-common/study.dtos";
import { willNeverHappen } from "healthex-common/utils";
import { getObservableT } from "../Infra/i18next";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { PatientConsentFlow } from "./PatientConsentUIManager";
import { getTranslatedStudyText } from "./TranslationService";
import { Disposer } from "../Infra/Disposer";
import { getPatientStatusChip, getPatientDateString } from "../patient.utils";
const t = getObservableT("patients");
export class PatientResearchStudyDetailsViewModelProps {
    constructor(container, modalStudyId, isInterested) {
        this.container = container;
        this.modalStudyId = modalStudyId;
        this.isInterested = isInterested;
    }
}
export class PatientResearchStudyDetailsViewModel {
    constructor(props) {
        var _a, _b;
        this.modalStudyId = null;
        this.isInterested = false;
        this.refreshConsentTimerId = null;
        this.disposer = new Disposer();
        this.studyRevokeConsentDialogOpen = false;
        this.studyRevokeConsentStudyId = null;
        this.byStudyConsentRequest = NOT_LOADED;
        this.patientStudyConsent = NOT_LOADED;
        this.isRevokeConsentButtonLoading = false;
        this.studyManager = props.container.studyManager;
        this.patientResearchDashboardUIManager = props.container.patientResearchDashboardUIManager;
        this.patientConsentUIManager = props.container.patientConsentUIManager;
        this.routingUIManager = props.container.routingUIManager;
        this.patientService = props.container.patientService;
        this.authManager = props.container.authManager;
        this.browserLanguageUIManager = props.container.browserLanguageUIManager;
        this.modalStudyId = (_a = props.modalStudyId) !== null && _a !== void 0 ? _a : null;
        this.isInterested = (_b = props.isInterested) !== null && _b !== void 0 ? _b : false;
    }
    run() {
        this.disposer.reaction(() => this.studyId, async () => {
            await this.loadConsentRequest();
        }, { fireImmediately: true });
        this.disposer.reaction(() => this.consentRequest, async () => {
            await this.loadPatientStudyConsent();
        }, { fireImmediately: true });
        this.disposer.reaction(() => this.patientStudyConsent, async () => {
            await this.startRefreshConsentTimerIfNeeded();
        }, { fireImmediately: true });
    }
    stop() {
        this.clearRefreshConsentTimerIfNeeded();
        this.disposer.dispose();
    }
    clearRefreshConsentTimerIfNeeded() {
        if (this.refreshConsentTimerId !== null) {
            window.clearInterval(this.refreshConsentTimerId);
        }
    }
    async loadConsentRequest() {
        var _a, _b, _c, _d;
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        this.byStudyConsentRequest =
            (_d = (await this.patientService.postSearchForPatientStudyConsents(this.authManager.id, {
                consentType: primaryConsentTypes,
                includeConsentingByLink: true,
                patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
                distinctPending: true,
                pending: this.isInterested,
                includePatientStudyConsentStatuses: !this.isInterested
                    ? [ConsentStatus.OPTED_IN, ConsentStatus.OPTED_OUT]
                    : undefined,
                studyId: (_b = this.studyId) !== null && _b !== void 0 ? _b : undefined,
                study: {
                    excludeStatuses: [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN],
                    excludeDeId: true,
                    patientConsentStatusSearch: StudyPatientConsentStatusSearch.INCLUDE_ALL_OPTED_IN,
                    patientId: (_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined,
                },
            }, 0)).results[0]) !== null && _d !== void 0 ? _d : null;
    }
    async loadPatientStudyConsent() {
        if (this.studyId === null)
            return;
        if (this.authManager.id === null)
            return;
        if (this.consentRequest === NOT_LOADED || this.consentRequest === null)
            return;
        if (this.consentState !== IdentifiedPatientState.ENROLLED)
            return;
        this.patientStudyConsent = await this.patientService.getPatientStudyConsent(this.authManager.id, this.studyId, true);
    }
    async startRefreshConsentTimerIfNeeded() {
        if (this.patientStudyConsent === NOT_LOADED || this.patientStudyConsent === null)
            return;
        this.clearRefreshConsentTimerIfNeeded();
        this.refreshConsentTimerId = window.setInterval(() => {
            if (this.proofOfConsentStatus === ScreenshotMetadataStatus.JOB_QUEUED ||
                this.proofOfConsentStatus === ScreenshotMetadataStatus.IN_PROGRESS) {
                void this.loadPatientStudyConsent();
            }
            else {
                this.clearRefreshConsentTimerIfNeeded();
            }
        }, 5000);
    }
    get studyId() {
        var _a, _b, _c;
        return (_c = (_a = this.modalStudyId) !== null && _a !== void 0 ? _a : (_b = this.routingUIManager.currentRouteParams) === null || _b === void 0 ? void 0 : _b.studyId) !== null && _c !== void 0 ? _c : null;
    }
    get consentRequest() {
        var _a, _b, _c, _d;
        if (this.byStudyConsentRequest === NOT_LOADED)
            return NOT_LOADED;
        const firstConsent = (_b = (_a = this.byStudyConsentRequest) === null || _a === void 0 ? void 0 : _a.consents[0]) !== null && _b !== void 0 ? _b : null;
        if (this.isInterested) {
            return ((_d = (_c = this.byStudyConsentRequest) === null || _c === void 0 ? void 0 : _c.consents.find((consent) => consent.state === IdentifiedPatientState.ENROLLING_INTERESTED ||
                consent.state === IdentifiedPatientState.ENROLLING_CONTACTED)) !== null && _d !== void 0 ? _d : firstConsent);
        }
        return firstConsent;
    }
    get consentState() {
        var _a, _b;
        if (this.consentRequest === NOT_LOADED)
            return null;
        return (_b = (_a = this.consentRequest) === null || _a === void 0 ? void 0 : _a.state) !== null && _b !== void 0 ? _b : null;
    }
    get study() {
        var _a, _b;
        if (this.byStudyConsentRequest === NOT_LOADED)
            return null;
        return (_b = (_a = this.byStudyConsentRequest) === null || _a === void 0 ? void 0 : _a.study) !== null && _b !== void 0 ? _b : null;
    }
    get patientFriendlyTitle() {
        var _a, _b, _c, _d;
        if (!this.study)
            return null;
        const translatedTitle = getTranslatedStudyText(this.study, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage);
        return (_d = (_b = translatedTitle !== null && translatedTitle !== void 0 ? translatedTitle : (_a = this.study) === null || _a === void 0 ? void 0 : _a.patientFriendlyTitle) !== null && _b !== void 0 ? _b : (_c = this.study) === null || _c === void 0 ? void 0 : _c.title) !== null && _d !== void 0 ? _d : null;
    }
    get reasonForRequest() {
        var _a, _b, _c, _d;
        if (!this.study)
            return null;
        const translatedTitle = getTranslatedStudyText(this.study, "purposeOfDataBriefEducation", this.browserLanguageUIManager.currentLanguage);
        return (_d = (_b = translatedTitle !== null && translatedTitle !== void 0 ? translatedTitle : (_a = this.study) === null || _a === void 0 ? void 0 : _a.patientFriendlyTitle) !== null && _b !== void 0 ? _b : (_c = this.study) === null || _c === void 0 ? void 0 : _c.title) !== null && _d !== void 0 ? _d : null;
    }
    get patientFriendlySummary() {
        var _a, _b, _c, _d;
        if (!this.study)
            return null;
        const translatedSummary = getTranslatedStudyText(this.study, "patientFriendlySummary", this.browserLanguageUIManager.currentLanguage);
        return (_d = (_b = translatedSummary !== null && translatedSummary !== void 0 ? translatedSummary : (_a = this.study) === null || _a === void 0 ? void 0 : _a.patientFriendlySummary) !== null && _b !== void 0 ? _b : (_c = this.study) === null || _c === void 0 ? void 0 : _c.briefSummary) !== null && _d !== void 0 ? _d : null;
    }
    get sponsorOrganizationName() {
        var _a, _b;
        return (_b = (_a = this.study) === null || _a === void 0 ? void 0 : _a.sponsorOrganizationName) !== null && _b !== void 0 ? _b : null;
    }
    get consentFromPatientStudyConsent() {
        var _a;
        if (this.patientStudyConsent === null ||
            this.patientStudyConsent === NOT_LOADED ||
            this.consentRequest === NOT_LOADED ||
            this.consentRequest === null) {
            return null;
        }
        const consentRequest = this.consentRequest;
        return (_a = this.patientStudyConsent.consents.find((consent) => consent.consentType === (consentRequest === null || consentRequest === void 0 ? void 0 : consentRequest.consentType))) !== null && _a !== void 0 ? _a : null;
    }
    get proofOfConsentStatus() {
        var _a, _b;
        if (this.consentFromPatientStudyConsent === null)
            return null;
        return (_b = (_a = this.consentFromPatientStudyConsent) === null || _a === void 0 ? void 0 : _a.proofOfConsentPdfStatus) !== null && _b !== void 0 ? _b : null;
    }
    get proofOfConsentInProgress() {
        return (this.proofOfConsentStatus === ScreenshotMetadataStatus.JOB_QUEUED ||
            this.proofOfConsentStatus === ScreenshotMetadataStatus.IN_PROGRESS);
    }
    get proofOfConsentStatusText() {
        if (this.study === null)
            return null;
        if (this.proofOfConsentStatus === null)
            return null;
        switch (this.proofOfConsentStatus) {
            case ScreenshotMetadataStatus.JOB_QUEUED:
            case ScreenshotMetadataStatus.IN_PROGRESS:
                return t("Your proof of consent is loading and this may take several minutes");
            case ScreenshotMetadataStatus.ERROR:
                return t("The proof of consent cannot be downloaded. Please try again later or contact {{ primaryContactName }} at ", {
                    primaryContactName: this.study.sitePrimaryContactName,
                });
            case ScreenshotMetadataStatus.COMPLETED:
                return null;
        }
        return willNeverHappen(this.proofOfConsentStatus);
    }
    get proofOfConsentUrl() {
        var _a, _b;
        return (_b = (_a = this.consentFromPatientStudyConsent) === null || _a === void 0 ? void 0 : _a.proofOfConsentPdfUrl) !== null && _b !== void 0 ? _b : "";
    }
    get showProofOfConsentButton() {
        var _a;
        if (this.consentFromPatientStudyConsent === null)
            return false;
        return ((_a = this.consentFromPatientStudyConsent) === null || _a === void 0 ? void 0 : _a.consentStatus) !== ConsentStatus.OPTED_OUT;
    }
    get proofOfConsentButtonEnabled() {
        return this.proofOfConsentStatus === ScreenshotMetadataStatus.COMPLETED && !!this.proofOfConsentUrl;
    }
    get isProofOfConsentError() {
        return this.proofOfConsentStatus === ScreenshotMetadataStatus.ERROR;
    }
    get isAIProject() {
        if (!this.study)
            return false;
        return this.study.projectType === ProjectType.AI_DATA_USE;
    }
    get studyInfo() {
        var _a;
        if (this.consentRequest === null || this.consentRequest === NOT_LOADED || this.study === null)
            return null;
        return {
            chip: getPatientStatusChip(this.consentRequest),
            title: this.patientFriendlyTitle,
            orgName: this.isAIProject ? this.study.aiToolName : this.study.addedByOrganization.name,
            date: getPatientDateString(this.consentRequest.timestamp),
            showUpdateRecords: !this.isAIProject &&
                this.consentRequest.state === IdentifiedPatientState.ENROLLED &&
                !!this.consentRequest.updateRecordsNeeded,
            showAIBanner: this.isAIProject && this.consentRequest.state === IdentifiedPatientState.ENROLLED,
            aiToolName: this.study.aiToolName,
            orgLogo: (_a = this.study.organizationIconUrl) !== null && _a !== void 0 ? _a : null,
        };
    }
    get requestInfo() {
        var _a;
        if (this.consentRequest === null || this.consentRequest === NOT_LOADED || this.study === null)
            return null;
        return {
            ...this.getReqestTitleAndText(),
            showDownloadUI: ((_a = this.consentRequest) === null || _a === void 0 ? void 0 : _a.state) === IdentifiedPatientState.ENROLLED,
            showReviewRequestUI: this.consentRequest.state
                ? [
                    IdentifiedPatientState.ENROLLING_INTERESTED,
                    IdentifiedPatientState.ENROLLING_CONTACTED,
                    IdentifiedPatientState.DECLINED_ENROLLMENT,
                    IdentifiedPatientState.REVOKED_ENROLLMENT,
                ].includes(this.consentRequest.state)
                : false,
        };
    }
    getReqestTitleAndText() {
        var _a;
        if (!this.consentState)
            return { title: null, text: null };
        switch (this.consentState) {
            case IdentifiedPatientState.ENROLLED:
                if (this.isAIProject) {
                    return {
                        title: t("You have consented to share your records"),
                        text: t("Your health records are now connected with {{ aiToolName }}.", { aiToolName: (_a = this.study) === null || _a === void 0 ? void 0 : _a.aiToolName }),
                    };
                }
                else {
                    return {
                        title: t("You have agreed to this request"),
                        text: t("As part of your participation in this study, your health records will be shared with the research team."),
                    };
                }
            case IdentifiedPatientState.ENROLLING_INTERESTED:
            case IdentifiedPatientState.ENROLLING_CONTACTED:
                return {
                    title: t("You have reviewed this request"),
                    text: t("You expressed interest in learning more about this study. You will be contacted by the research team soon answer any questions you may have."),
                    reengageText: t("Want to see the request again?"),
                };
            case IdentifiedPatientState.DECLINED_ENROLLMENT:
                return {
                    title: t("You have declined this request"),
                    text: t("Your health records will not be shared."),
                    reengageText: t("Changed your mind? Get involved again by reviewing the request."),
                };
            case IdentifiedPatientState.REVOKED_ENROLLMENT:
                return {
                    title: t("You have withdrawn your permission for this request"),
                    text: t("Your health records will no longer be shared."),
                    reengageText: t("Changed your mind? Get involved again by reviewing the request."),
                };
            default:
                return { title: null, text: null };
        }
    }
    showStudyRevokeConsentDialog() {
        this.studyRevokeConsentDialogOpen = true;
        this.studyRevokeConsentStudyId = this.studyId;
    }
    hideStudyRevokeConsentDialog() {
        this.studyRevokeConsentDialogOpen = false;
        this.studyRevokeConsentStudyId = null;
    }
    startConsentFlow() {
        if (this.studyId === null)
            return;
        this.patientConsentUIManager.startConsentFlow(this.studyId, PatientConsentFlow.ENROLLMENT, null, null);
    }
    dataUpdateConsentFlow() {
        if (!this.studyId)
            return;
        this.patientConsentUIManager.dataUpdateConsentFlow(this.studyId);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentDialogOpen", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientResearchStudyDetailsViewModel.prototype, "studyRevokeConsentStudyId", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientResearchStudyDetailsViewModel.prototype, "byStudyConsentRequest", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientResearchStudyDetailsViewModel.prototype, "patientStudyConsent", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], PatientResearchStudyDetailsViewModel.prototype, "isRevokeConsentButtonLoading", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "studyId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "consentRequest", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "consentState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "study", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "patientFriendlyTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "reasonForRequest", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "patientFriendlySummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "sponsorOrganizationName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "consentFromPatientStudyConsent", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentStatus", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentInProgress", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentStatusText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentUrl", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "showProofOfConsentButton", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "proofOfConsentButtonEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "isProofOfConsentError", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "isAIProject", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "studyInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientResearchStudyDetailsViewModel.prototype, "requestInfo", null);
